import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './MainboardReplacerForm.module.scss';
import { ReplacementStatus } from '../../../services/api/api-client.types.ts';
import {
  getAxios,
  getBaseUrl,
  MainboardCertificateQuery,
} from 'services/api/api-client.ts';
import {
  Button,
  ButtonColor,
} from '../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import ResetIcon from 'assets/icons/reset.svg?react';
import CloudIcon from 'assets/icons/small-cloud.svg?react';
import {
  LoadingPage,
  LoadingType,
} from '../../../components/animations/LoadingPage.tsx';
import clsx from 'clsx';
import { formatDetailedDate } from '../../../helpers/abas-helper.ts';

export type ReplacementResultProps = {
  operationCreatedAt: Date | null;
  operationId: number;
  tryAgain: () => void;
  generateNew: () => void;
  trackMainboardEvent: (action: string, name?: string) => void;
};

export const MainboardReplacementResult = (props: ReplacementResultProps) => {
  const {
    operationCreatedAt,
    operationId,
    tryAgain,
    generateNew,
    trackMainboardEvent,
  } = props;

  const [countdown, setCountdown] = useState(10);
  const [status, setStatus] = useState<ReplacementStatus | undefined>();
  const [loadingStatus, setLoadingStatus] = useState<LoadingType>('loading');
  const downloadedCertRef = useRef<string | null>(null);

  const i18n = useScopedTranslation('Page.MainboardReplacer');

  const resultQuery = MainboardCertificateQuery.useGetResultQuery(
    { operationId: operationId },
    { refetchInterval: status === ReplacementStatus.Pending ? 10000 : false },
  );

  const DownloadCertificate = useCallback(
    async (certificateName: string) => {
      const url =
        getBaseUrl() +
        '/api/mainboard-certificate/cert?fileName=' +
        encodeURIComponent(certificateName);
      const axios = getAxios();
      const res = await axios.get(url, {
        responseType: 'blob',
      });

      const blob = res.data as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${certificateName}`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(downloadUrl);
      trackMainboardEvent('Certificate downloaded', certificateName);
    },
    [trackMainboardEvent],
  );

  useEffect(() => {
    setStatus(resultQuery.data?.status);
  }, [resultQuery.data?.status]);

  useEffect(() => {
    if (status && status !== ReplacementStatus.Pending) {
      setLoadingStatus('completed');
      setCountdown(10);
      return;
    }

    setCountdown(10);

    const id = window.setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 10 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [status]);

  const onRefresh = () => {
    void resultQuery.refetch();
    setCountdown(10);
  };

  useEffect(() => {
    if (
      resultQuery.data?.status === ReplacementStatus.Success &&
      !!resultQuery.data.certificateFileName &&
      downloadedCertRef.current !== resultQuery.data.certificateFileName
    ) {
      downloadedCertRef.current = resultQuery.data.certificateFileName;
      void DownloadCertificate(resultQuery.data.certificateFileName);
    }
  }, [
    resultQuery.data?.status,
    resultQuery.data?.certificateFileName,
    DownloadCertificate,
  ]);

  const isSuccess = resultQuery.data?.status === ReplacementStatus.Success;

  return (
    <div data-test-id="mainboard-replacer-result">
      <LoadingPage
        className={styles.loadingPage}
        loadingStatus={loadingStatus}
        afterLoading={isSuccess ? 'success' : 'failure'}
        titleLoadingContent={
          <PendingComponent
            iccid={resultQuery.data?.iccid}
            imei={resultQuery.data?.imei}
            isFetched={resultQuery.isFetched}
            countdown={countdown}
            onRefresh={onRefresh}
          />
        }
        titleContent={
          isSuccess ? (
            <div>{i18n.t('success')}</div>
          ) : (
            <div>{i18n.t('failure')}</div>
          )
        }
        bodyContent={
          <div className={styles.width420}>
            {isSuccess ? (
              <div className={clsx(styles.columnContainer12, styles.greyText)}>
                {operationCreatedAt ? (
                  <div data-test-id="success-duplicated">
                    {i18n.t('success_duplicated', {
                      number: resultQuery.data?.serialNumber,
                      date: formatDetailedDate(operationCreatedAt, i18n.i18n),
                    })}
                  </div>
                ) : (
                  <div data-test-id="success-default">
                    {i18n.t('success_default', {
                      number: resultQuery.data?.serialNumber,
                    })}
                  </div>
                )}

                <div>{i18n.t('success_default_download')}</div>
              </div>
            ) : (
              <div className={clsx(styles.columnContainer, styles.greyText)}>
                <div>{i18n.t('failure_text')}</div>
                <div
                  className={clsx(
                    styles.greyText,
                    styles.columnContainer12,
                    styles.marginTop12,
                  )}
                >
                  <div className={styles.rowContainer4}>
                    <div>{i18n.t('operation_id')}</div>
                    <div className={clsx(styles.p1Med, styles.maxHeight320)}>
                      {resultQuery.data?.requestId}
                    </div>
                  </div>

                  <div className={styles.rowContainer4}>
                    <div>{i18n.t('error')}</div>
                    <div className={clsx(styles.p1Med, styles.maxHeight320)}>
                      {resultQuery.data?.errorMessage}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        }
        footerContent={
          <>
            {isSuccess ? (
              resultQuery.data && (
                <div className={styles.columnContainer12}>
                  <Button
                    testId={'mainboard-download-certificate-button'}
                    title={resultQuery.data!.certificateFileName!}
                    className={clsx(styles.loadingButton, styles.width294)}
                    iconClassName={styles.icon}
                    onClick={() => {
                      void DownloadCertificate(
                        resultQuery.data!.certificateFileName!,
                      );
                    }}
                  >
                    <CloudIcon />
                  </Button>
                  <Button
                    testId={'mainboard-generate-new-button'}
                    className={styles.width294}
                    color={ButtonColor.Secondary}
                    title={i18n.t('generate_new_button')}
                    onClick={() => {
                      setStatus(undefined);
                      generateNew();
                    }}
                  />
                </div>
              )
            ) : (
              <Button
                testId={'mainboard-try-again-button'}
                title={i18n.t('try_again_button')}
                className={styles.loadingButton}
                onClick={() => {
                  tryAgain();
                }}
              />
            )}
          </>
        }
      />
    </div>
  );
};

type PendingTitleProps = {
  iccid?: string | null;
  imei?: string | null;
  countdown: number;
  onRefresh: () => void;
  isFetched: boolean;
};

const PendingComponent = (props: PendingTitleProps) => {
  const { iccid, imei, countdown, onRefresh, isFetched } = props;
  const i18n = useScopedTranslation('Page.MainboardReplacer');
  return (
    <div
      className={clsx(!isFetched && styles.offFlex, styles.loadingContainer)}
    >
      <div className={styles.h1}>{i18n.t('pending')}</div>
      {isFetched && (
        <>
          <div className={clsx(styles.h3, styles.marginTop12)}>
            {i18n.t('sim_card_activation')}
          </div>

          <div className={styles.greyText}>
            {i18n.t('try_refresh', { seconds: countdown })}
          </div>

          <div
            className={clsx(
              styles.greyText,
              styles.rowContainer24,
              styles.marginTop12,
            )}
          >
            <div className={styles.rowContainer4}>
              <div>{i18n.t('iccid')}</div>
              <div className={styles.p1Med}>{iccid}</div>
            </div>

            <div className={styles.rowContainer4}>
              <div>{i18n.t('imei')}</div>
              <div className={styles.p1Med}>{imei}</div>
            </div>
          </div>

          <Button
            testId={'mainboard-refresh-button'}
            disabled={countdown > 8}
            title={i18n.t('refresh_button')}
            className={clsx(styles.refreshButton)}
            onClick={onRefresh}
          >
            <ResetIcon />
          </Button>
        </>
      )}
    </div>
  );
};
